import http from 'hub-http/clients/apiClient';
import { createStack } from 'hub-http';
import promiseClient from 'hub-http/adapters/promiseClient';
import hubapiStack from 'hub-http/stacks/hubapi';
import { retry } from 'hub-http/middlewares/core';
import campaignsRpcClient from '../../api/campaignsRpcClient';
import { associateWithCampaignsRpc, dissociateFromCampaignsRpc, setAssociationsRpc } from 'campaigns-lib/__generated__/chirp/com/hubspot/campaigns/association/rpc/CampaignsAssociationRpc';
import { mapObjectType } from '../hooks/useFetchObjectPropertyDefinitions';

// Retry fetching CRM object if no results are returned
// due to the CRM Lag of the object creation.
const httpCrmObjectFetchRetry = promiseClient(createStack(retry(response => response.responseJSON.results.length === 0, {
  maxRetries: 10
}), hubapiStack));
function fetchObjectFromCrm({
  objectType,
  crmIdProperty,
  selectedAssetCrmId
}) {
  return httpCrmObjectFetchRetry.post(`/crm-search/search`, {
    data: {
      count: 1,
      offset: 0,
      objectTypeId: objectType,
      requestOptions: {
        allPropertiesFetchMode: 'latest_version',
        includeAllProperties: true
      },
      filterGroups: [{
        filters: [{
          operator: 'EQ',
          property: crmIdProperty,
          value: selectedAssetCrmId
        }]
      }]
    }
  }).then(response => {
    return response.results[0].objectId;
  });
}
export function saveCampaign({
  objectType,
  crmIdProperty,
  selectedAssetCrmId,
  currentCampaignGuids,
  updatedCampaignGuids,
  onSuccess = () => {},
  onFailure = () => {}
}) {
  const searchRequest = crmIdProperty ? fetchObjectFromCrm({
    objectType,
    crmIdProperty,
    selectedAssetCrmId
  }) : Promise.resolve(selectedAssetCrmId);
  return searchRequest.then(assetObjectId => {
    const updatedCampaignGuidsSet = new Set(updatedCampaignGuids);
    const currentCampaignGuidsSet = new Set(currentCampaignGuids);
    const campaignsToAssociate = updatedCampaignGuids.filter(guid => !currentCampaignGuidsSet.has(guid)).filter(Boolean);
    const campaignsToDisassociate = currentCampaignGuids.filter(guid => !updatedCampaignGuidsSet.has(guid)).filter(Boolean);
    const assetObjectType = mapObjectType(objectType);
    const crmAssetIds = [Number(assetObjectId)];
    const dissociatePromise = campaignsToDisassociate.length > 0 ? campaignsRpcClient.call(dissociateFromCampaignsRpc, {
      request: {
        campaignGuids: campaignsToDisassociate,
        assetObjectType,
        crmAssetIds
      }
    }) : Promise.resolve();
    if (campaignsToAssociate.length > 0) {
      return dissociatePromise.then(() => campaignsRpcClient.call(associateWithCampaignsRpc, {
        request: {
          campaignGuids: campaignsToAssociate,
          assetObjectType,
          crmAssetIds
        }
      })).then(onSuccess).catch(onFailure);
    }
    return dissociatePromise.then(onSuccess).catch(onFailure);
  });
}
export function setOneToManyAssociations({
  objectType,
  assetId,
  crmIdProperty,
  campaignGuids,
  primaryCampaignGuid
}) {
  const resolveId = crmIdProperty && crmIdProperty !== 'hs_object_id' ? fetchObjectFromCrm({
    objectType,
    crmIdProperty,
    selectedAssetCrmId: assetId
  }) : Promise.resolve(Number(assetId));
  return resolveId.then(resolvedAssetId => campaignsRpcClient.call(setAssociationsRpc, {
    request: {
      assetObjectTypeId: objectType,
      crmAssetIds: [resolvedAssetId],
      campaignGuids,
      primaryCampaignGuid
    }
  }));
}
export function deleteCampaignsAssociation({
  fromObjectId,
  campaignObjectId,
  objectType
}) {
  return campaignsRpcClient.call(dissociateFromCampaignsRpc, {
    request: {
      campaignGuids: [],
      campaignCrmId: campaignObjectId,
      assetObjectType: mapObjectType(objectType),
      crmAssetIds: [fromObjectId]
    }
  });
}
export function fetchAndDeleteCampaignsAssociation({
  associationId,
  objectType,
  crmIdProperty,
  assetId
}) {
  const associationCategoryAndIdPair = `0-${associationId}`;
  return http.post('/crm-search/search', {
    data: {
      count: 1,
      objectTypeId: objectType,
      offset: 0,
      requestOptions: {
        properties: ['hs_object_id']
      },
      filterGroups: [{
        filters: [{
          operator: 'EQ',
          property: crmIdProperty,
          value: assetId
        }]
      }],
      associationPreviews: {
        [associationCategoryAndIdPair]: {
          properties: ['hs_object_id']
        }
      }
    }
  }).then(response => {
    const crmObject = response === null || response === void 0 ? void 0 : response.results[0];
    if (crmObject) {
      var _associatedObjects$as;
      const {
        objectId,
        associatedObjects
      } = crmObject;
      const campaignObjectId = associatedObjects === null || associatedObjects === void 0 || (_associatedObjects$as = associatedObjects[associationCategoryAndIdPair]) === null || _associatedObjects$as === void 0 || (_associatedObjects$as = _associatedObjects$as[0]) === null || _associatedObjects$as === void 0 ? void 0 : _associatedObjects$as.objectId;
      if (campaignObjectId && objectId) {
        return deleteCampaignsAssociation({
          fromObjectId: objectId,
          campaignObjectId,
          objectType
        });
      }
    }
  });
}